import React from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Printer, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, getLogoSVG } from "@/components/Logo";
import { DOWNLOADS } from "@/lib/testIds";

const LETTERHEAD_URL = "/Campusco_Letterhead_Template.docx";

const downloadFile = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const svgToPng = async (svgString, width) => {
  return new Promise((resolve) => {
    const svg = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ratio = img.height / img.width;
      canvas.width = width;
      canvas.height = width * ratio;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.src = url;
  });
};

const logoVariants = [
  { key: "primary", label: "Primary", desc: "Full colour on light backgrounds.", bg: "bg-background", testId: DOWNLOADS.logoPrimary },
  { key: "dark", label: "Dark Mono", desc: "For light or paper backgrounds.", bg: "bg-background", testId: DOWNLOADS.logoDark },
  { key: "light", label: "Light Mono", desc: "For dark surfaces & photography.", bg: "bg-primary", testId: DOWNLOADS.logoLight },
  { key: "mono", label: "One-Colour Black", desc: "Print, faxes, embossing.", bg: "bg-background", testId: DOWNLOADS.logoMono },
  { key: "icon", label: "Icon Mark", desc: "Favicons, avatars, social.", bg: "bg-background", testId: DOWNLOADS.logoIcon },
];

/** Business-only downloads — rendered inside the Admin panel (not public). */
export const BusinessAssets = () => {
  const handleDownload = async (variant, format) => {
    const svg = getLogoSVG(variant, false);
    const fname = `campusco-logo-${variant}`;
    if (format === "svg") {
      downloadFile(svg, `${fname}.svg`, "image/svg+xml");
    } else {
      const blob = await svgToPng(svg, variant === "icon" ? 512 : 1600);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fname}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Letterhead */}
        <div className="border border-border bg-background p-6">
          <FileSignature className="w-8 h-8 text-secondary" strokeWidth={1.5} />
          <p className="mt-4 overline text-secondary">Stationery</p>
          <h3 className="mt-2 font-serif text-2xl">Official letterhead (.docx)</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            European-style MS Word letterhead with branding, Reg. No. 2020/774579/07 and director details.
          </p>
          <Button asChild className="mt-5 rounded-sm h-11 bg-primary text-primary-foreground" data-testid="download-letterhead-docx">
            <a href={LETTERHEAD_URL} download="Campusco_Letterhead_Template.docx">
              <Download className="w-4 h-4 mr-2" /> Download Letterhead
            </a>
          </Button>
        </div>

        {/* Brochure */}
        <div className="border border-border bg-background p-6">
          <FileText className="w-8 h-8 text-secondary" strokeWidth={1.5} />
          <p className="mt-4 overline text-secondary">Services Brochure</p>
          <h3 className="mt-2 font-serif text-2xl">Full services deck (PDF)</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Company overview, accreditations, services and footprint — opens a print-ready page.
          </p>
          <Button asChild className="mt-5 rounded-sm h-11 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid={DOWNLOADS.brochurePdf}>
            <Link to="/brochure" target="_blank" rel="noopener">
              <Printer className="w-4 h-4 mr-2" /> Open Brochure
            </Link>
          </Button>
        </div>
      </div>

      {/* Logo pack */}
      <div>
        <p className="overline text-secondary">Brand Assets</p>
        <h3 className="mt-2 font-serif text-2xl">Campusco logo pack</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">SVG (vector) or PNG (high-res). Use with clear space; never distort or recolour.</p>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          {logoVariants.map((v) => (
            <div key={v.key} className="border border-border bg-background">
              <div className={`${v.bg} p-8 aspect-[2/1] flex items-center justify-center border-b border-border`}>
                <Logo variant={v.key} className="max-h-14 w-auto" />
              </div>
              <div className="p-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-serif text-lg">{v.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{v.desc}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-sm" onClick={() => handleDownload(v.key, "svg")} data-testid={`${v.testId}-svg`}>
                    <Download className="w-4 h-4 mr-1.5" /> SVG
                  </Button>
                  <Button className="rounded-sm bg-primary text-primary-foreground" onClick={() => handleDownload(v.key, "png")} data-testid={`${v.testId}-png`}>
                    <Download className="w-4 h-4 mr-1.5" /> PNG
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
